import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { parseSearchQuery } from '../hooks/useAdvancedSearch';
import { ulid } from 'ulid';

export interface WorkerGroup {
  id: string;
  name: string;
  description: string;
  members: string[]; // Worker ID 배열
  teamLead?: string; // Worker ID
  specialties: string[];
  status: 'active' | 'inactive';
  createdAt: string;
  updatedAt: string;
  totalProjects: number;
  avgRating: number;
}

interface WorkerGroupStore {
  groups: WorkerGroup[];
  isLoading: boolean;
  searchQuery: string;
  statusFilter: 'all' | 'active' | 'inactive';
  
  // Actions
  loadGroups: () => Promise<void>;
  createGroup: (group: Omit<WorkerGroup, 'id' | 'createdAt' | 'updatedAt' | 'totalProjects' | 'avgRating'>) => Promise<void>;
  updateGroup: (id: string, updates: Partial<WorkerGroup>) => Promise<void>;
  deleteGroup: (id: string) => Promise<void>;
  addMemberToGroup: (groupId: string, workerId: string) => Promise<void>;
  removeMemberFromGroup: (groupId: string, workerId: string) => Promise<void>;
  setSearchQuery: (query: string) => void;
  setStatusFilter: (status: 'all' | 'active' | 'inactive') => void;
  getFilteredGroups: () => WorkerGroup[];
}

// 샘플 데이터
const sampleGroups: WorkerGroup[] = [
  {
    id: '01JGHPRY6X4EKFRQ4RVTXSYQ5P',
    name: '한영 번역팀',
    description: '한국어-영어 전문 번역 그룹',
    members: ['01JGHPRFRX3NKFRQ8ZVTXSYQ7X', '01JGHPRHVZ9QKFRP6CVTXSYQ9Z'],
    teamLead: '01JGHPRHVZ9QKFRP6CVTXSYQ9Z',
    specialties: ['한국어', '영어', '번역', '검수'],
    status: 'active',
    createdAt: '2023-01-20',
    updatedAt: '2024-01-15',
    totalProjects: 45,
    avgRating: 4.8
  },
  {
    id: '01JGHPRZ8Z7FKFRN6SVTXSYQ6Q',
    name: '한일 번역팀',
    description: '한국어-일본어 전문 번역 그룹',
    members: ['01JGHPRGTW6PKFRN4BVTXSYQ8Y', '01JGHPRKZ5FSKFRQ2EVTXSYQ2B'],
    teamLead: '01JGHPRGTW6PKFRN4BVTXSYQ8Y',
    specialties: ['한국어', '일본어', '번역', '통역'],
    status: 'active',
    createdAt: '2023-02-15',
    updatedAt: '2024-01-10',
    totalProjects: 32,
    avgRating: 4.6
  },
  {
    id: '01JGHPS1B29GKFRP8TVTXSYQ7R',
    name: '한중 번역팀',
    description: '한국어-중국어 전문 번역 그룹',
    members: ['01JGHPRJX2CRJKFR8DVTXSYQ1A'],
    teamLead: '01JGHPRJX2CRJKFR8DVTXSYQ1A',
    specialties: ['한국어', '중문', '번역', '전사'],
    status: 'active',
    createdAt: '2023-03-10',
    updatedAt: '2024-01-12',
    totalProjects: 28,
    avgRating: 4.7
  },
  {
    id: '01JGHPS2D4BHKFRQ2UVTXSYQ8S',
    name: '전사 전문팀',
    description: '오디오 전사 전문 그룹',
    members: ['01JGHPRJX2CRJKFR8DVTXSYQ1A', '01JGHPRKZ5FSKFRQ2EVTXSYQ2B'],
    teamLead: '01JGHPRKZ5FSKFRQ2EVTXSYQ2B',
    specialties: ['전사', '오디오 처리', '타임코딩'],
    status: 'active',
    createdAt: '2023-04-05',
    updatedAt: '2024-01-08',
    totalProjects: 67,
    avgRating: 4.5
  },
  {
    id: '01JGHPS3F6CJKFRN4VVTXSYQ9T',
    name: '검수 전문팀',
    description: '품질 검수 전문 그룹',
    members: ['01JGHPRFRX3NKFRQ8ZVTXSYQ7X', '01JGHPRGTW6PKFRN4BVTXSYQ8Y', '01JGHPRHVZ9QKFRP6CVTXSYQ9Z'],
    teamLead: '01JGHPRFRX3NKFRQ8ZVTXSYQ7X',
    specialties: ['검수', '품질관리', '편집'],
    status: 'active',
    createdAt: '2023-05-01',
    updatedAt: '2024-01-20',
    totalProjects: 89,
    avgRating: 4.9
  },
  {
    id: '01JGHPS4H8DKKFRP6WVTXSYQ1U',
    name: '유럽어 번역팀',
    description: '유럽 언어 전문 번역 그룹',
    members: ['01JGHPRM18HTKFRN4FVTXSYQ3C'],
    teamLead: '01JGHPRM18HTKFRN4FVTXSYQ3C',
    specialties: ['스페인어', '영어', '현지화', '번역'],
    status: 'inactive',
    createdAt: '2023-06-15',
    updatedAt: '2023-12-20',
    totalProjects: 15,
    avgRating: 4.3
  }
];

export const useWorkerGroupStore = create<WorkerGroupStore>()(
  devtools(
    (set, get) => ({
      groups: sampleGroups,
      isLoading: false,
      searchQuery: '',
      statusFilter: 'all',

      loadGroups: async () => {
        set({ isLoading: true });
        try {
          // API 호출 시뮬레이션
          await new Promise(resolve => setTimeout(resolve, 500));
          // 실제로는 API에서 데이터를 가져옴
          set({ groups: sampleGroups });
        } catch (error) {
          console.error('그룹 목록 로딩 실패:', error);
        } finally {
          set({ isLoading: false });
        }
      },

      createGroup: async (groupData) => {
        set({ isLoading: true });
        try {
          const newGroup: WorkerGroup = {
            ...groupData,
            id: ulid(),
            createdAt: new Date().toISOString().split('T')[0],
            updatedAt: new Date().toISOString().split('T')[0],
            totalProjects: 0,
            avgRating: 0
          };
          
          set(state => ({
            groups: [...state.groups, newGroup]
          }));
          
          console.log('그룹 생성 완료:', newGroup);
        } catch (error) {
          console.error('그룹 생성 실패:', error);
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      updateGroup: async (id, updates) => {
        set({ isLoading: true });
        try {
          set(state => ({
            groups: state.groups.map(group =>
              group.id === id 
                ? { ...group, ...updates, updatedAt: new Date().toISOString().split('T')[0] }
                : group
            )
          }));
          
          console.log('그룹 수정 완료:', id, updates);
        } catch (error) {
          console.error('그룹 수정 실패:', error);
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      deleteGroup: async (id) => {
        set({ isLoading: true });
        try {
          set(state => ({
            groups: state.groups.filter(group => group.id !== id)
          }));
          
          console.log('그룹 삭제 완료:', id);
        } catch (error) {
          console.error('그룹 삭제 실패:', error);
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      addMemberToGroup: async (groupId, workerId) => {
        set({ isLoading: true });
        try {
          set(state => ({
            groups: state.groups.map(group =>
              group.id === groupId
                ? { 
                    ...group, 
                    members: [...group.members.filter(id => id !== workerId), workerId],
                    updatedAt: new Date().toISOString().split('T')[0]
                  }
                : group
            )
          }));
          
          console.log('그룹 멤버 추가 완료:', groupId, workerId);
        } catch (error) {
          console.error('그룹 멤버 추가 실패:', error);
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      removeMemberFromGroup: async (groupId, workerId) => {
        set({ isLoading: true });
        try {
          set(state => ({
            groups: state.groups.map(group =>
              group.id === groupId
                ? { 
                    ...group, 
                    members: group.members.filter(id => id !== workerId),
                    updatedAt: new Date().toISOString().split('T')[0]
                  }
                : group
            )
          }));
          
          console.log('그룹 멤버 제거 완료:', groupId, workerId);
        } catch (error) {
          console.error('그룹 멤버 제거 실패:', error);
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      setSearchQuery: (query) => {
        set({ searchQuery: query });
      },

      setStatusFilter: (status) => {
        set({ statusFilter: status });
      },

      getFilteredGroups: () => {
        const { groups, searchQuery, statusFilter } = get();
        
        return groups.filter(group => {
          // 상태 필터
          if (statusFilter !== 'all' && group.status !== statusFilter) {
            return false;
          }
          
          // 검색 쿼리
          if (searchQuery) {
            const tokens = parseSearchQuery(searchQuery);
            
            // 모든 토큰이 AND 조건으로 만족해야 함
            return tokens.every(token => {
              const value = token.value.toLowerCase();
              
              if (token.type === 'key-value' && token.key) {
                const key = token.key.toLowerCase();
                
                // 특정 키 매핑
                switch (key) {
                  case '그룹명':
                  case 'name':
                    return group.name.toLowerCase().includes(value);
                  
                  case '설명':
                  case 'description':
                    return group.description.toLowerCase().includes(value);
                  
                  case '전문분야':
                  case 'specialization':
                  case 'specialties':
                    return group.specialties.some(specialty => specialty.toLowerCase().includes(value));
                  
                  case '상태':
                  case 'status':
                    return group.status.toLowerCase().includes(value);
                  
                  default:
                    // 일반 검색으로 처리
                    return (
                      group.name.toLowerCase().includes(value) ||
                      group.description.toLowerCase().includes(value) ||
                      group.specialties.some(specialty => specialty.toLowerCase().includes(value))
                    );
                }
              } else {
                // 일반 검색 (모든 필드)
                return (
                  group.name.toLowerCase().includes(value) ||
                  group.description.toLowerCase().includes(value) ||
                  group.specialties.some(specialty => specialty.toLowerCase().includes(value))
                );
              }
            });
          }
          
          return true;
        });
      }
    }),
    { name: 'worker-group-store' }
  )
);
